/* The PDP catalog — designs/pdp-catalog.md. The Product Development Plan
   defined ONCE, org-wide: the deliverables matrix, the per-phase exit
   criteria and the gates every product is measured against. This module is
   the cross-design contract (PC2) that products-pdp.md and kpi-feed-seam.md
   read structurally; the seed lives beside it in pdp-seed.ts (PC15). Pure
   rules only — the store and the routes call these, the client says the same
   reasons sooner (AA2). */

import type { Stamp, UserRef } from "./organizer-roles";
import { ENTERED_SRC } from "./projects";
import { normalizeName, type Role } from "./roles";

export type PdpPhase = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const PDP_PHASES: readonly PdpPhase[] = [0, 1, 2, 3, 4, 5, 6];
export const PDP_VERSION = "2.5";
export type PdpVersion = typeof PDP_VERSION;

/* the sheet's stage / operational-scope labels — the only phase copy the
   client shows (PC2; Sheet 1 operational scope + Sheet 6 stages) */
export const PDP_PHASE_NAMES: Record<PdpPhase, string> = {
  0: "Learning",
  1: "PoC",
  2: "Prototype for pilot",
  3: "Prototype in production",
  4: "Maturation",
  5: "Product in production",
  6: "Scaler",
};

/* The import stamp every seed row carries — the spreadsheet is the source,
   the extract date is the date, and there is NO recorder (PC7). */
export const IMPORT_SRC =
  "PDP 2.5 Engineering Deliverables Matrix - DRAFT.xlsx (SharePoint productppps), imported Sep 22, 2026";
export const IMPORT_DATE = "2026-09-22T00:00:00.000Z";

export interface PdpDeliverable {
  id: string;
  version: PdpVersion;
  /* Sheet 2's heading verbatim, e.g. "02 - Safety & Compliance" */
  swimlane: string;
  path: string;
  name: string;
  /* the phase this row is owed in */
  phase: PdpPhase;
  /* owed once per phase from `phase` onward — false on every imported row
     (PC5); products-pdp derives the slots */
  iterate: boolean;
  /* Roles-catalog ids (PC6); [] = not recorded (red) */
  ownerRoleIds: string[];
  reviewerRoleIds: string[];
  consultedRoleIds: string[];
  /* out of the owed set from the revision's date (PC10); absent = standing */
  retired?: boolean;
  recorded: Stamp;
}

export interface PdpTarget {
  op: ">=" | "<=" | ">" | "<";
  value: number;
  unit: string;
}

export interface PdpCriterion {
  id: string;
  version: PdpVersion;
  phase: PdpPhase;
  area: string;
  /* null = not recorded (red) */
  text: string | null;
  unconfirmed: boolean;
  /* machine-readable only when the text carries an operator and a number */
  target: PdpTarget | null;
  recorded: Stamp;
}

export interface PdpGate {
  /* "gate-0" | "exit-1" | "gate-2" | "exit-3" | "exit-4" | "gate-5" */
  id: string;
  version: PdpVersion;
  afterPhase: PdpPhase;
  label: string;
  /* a Roles-catalog id, or null = not recorded (red) */
  approverRoleId: string | null;
  recorded: Stamp;
}

/* PC9: the six exit rows of the Goals sheet (System reliability KPI split
   into its two measures) then the ten expectation areas, in sheet order */
export const PDP_CRITERION_AREAS: readonly string[] = [
  "ConOps automated",
  "Revenue",
  "System productivity KPI",
  "System reliability KPI · unscheduled downtime",
  "System reliability KPI · warranty rate",
  "Performance",
  "Exit template timing",
  "Operational scope",
  "Workflow / ConOps",
  "Requirements",
  "Safety",
  "Design",
  "Validation",
  "BOM",
  "Supply chain / mfg",
  "Deployment docs",
  "KPIs",
];

/* ---- ids (PC3/PC9) ---- */

/* normalizeName, then every run outside [a-z0-9] becomes one dash — paths
   carry "/" and "&", so slugId's space-only rule is not enough here */
export const pdpSlug = (text: string): string =>
  normalizeName(text)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/* "02 - Safety & Compliance" → "02"; a hand-added swimlane with no leading
   number is slugged whole */
const swimlaneKey = (swimlane: string): string => {
  const numbered = /^\s*(\d+)/.exec(swimlane);
  return numbered ? numbered[1]! : pdpSlug(swimlane);
};

export const pdpDeliverableId = (swimlane: string, path: string, phase: PdpPhase, name: string): string =>
  `${swimlaneKey(swimlane)}-${pdpSlug(path)}-p${phase}-${pdpSlug(name)}`;

export const pdpCriterionId = (phase: PdpPhase, area: string): string => `c-p${phase}-${pdpSlug(area)}`;

/* ---- inputs and edits (PC11) ---- */

export interface PdpDeliverableInput {
  swimlane: string;
  path: string;
  name: string;
  phase: PdpPhase;
  iterate: boolean;
  ownerRoleIds: string[];
  reviewerRoleIds: string[];
  consultedRoleIds: string[];
}

export type PdpDeliverableEdit = Partial<PdpDeliverableInput> & { retired?: boolean };

export interface PdpCriterionInput {
  phase: PdpPhase;
  area: string;
  text: string | null;
  unconfirmed: boolean;
  target: PdpTarget | null;
}

/* phase and area are the criterion's IDENTITY (its id is minted from them,
   PC9) — an edit changes the slot's content, never which slot it is */
export type PdpCriterionEdit = Partial<Pick<PdpCriterionInput, "text" | "unconfirmed" | "target">>;

export interface PdpGateEdit {
  label?: string;
  approverRoleId?: string | null;
}

const NAME_MAX = 200;
const TEXT_MAX = 2000;
const TARGET_OPS: readonly string[] = [">=", "<=", ">", "<"];
const TARGET_PARTIAL = "A target needs an operator, a value and a unit — fill all three or clear them.";

const collapse = (text: string): string => text.trim().replace(/\s+/g, " ");

const isPhase = (value: unknown): value is PdpPhase =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 6;

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");

/* every id must be in the Roles catalog (settled decision 3 applied to
   roles). A retired role stays valid where it is ALREADY recorded (PC6) —
   `recorded` names those ids — but is not accepted as a new assignment: the
   picker does not offer it, and the server says the same. */
const roleReason = (ids: string[], roles: Role[], recorded: readonly string[] = []): string | null => {
  for (const id of ids) {
    const role = roles.find((r) => r.id === id);
    if (!role) return `"${id}" is not in the Roles catalog`;
    if (role.retired && !recorded.includes(id)) return `"${role.name}" is retired — bring it back on Roles first`;
  }
  return null;
};

const NOTHING = "nothing to change";

/* ---- deliverables (PC3/PC4/PC6) ---- */

export const standing = <T extends { retired?: boolean }>(rows: T[]): T[] => rows.filter((r) => !r.retired);

export function validatePdpDeliverableInput(
  input: {
    swimlane: unknown;
    path: unknown;
    name: unknown;
    phase: unknown;
    iterate: unknown;
    ownerRoleIds: unknown;
    reviewerRoleIds: unknown;
    consultedRoleIds: unknown;
  },
  rows: PdpDeliverable[],
  roles: Role[],
  exceptId?: string,
): string | null {
  const { swimlane, path, name, phase, iterate } = input;
  if (typeof swimlane !== "string" || !swimlane.trim()) return "a swimlane is required";
  if (typeof path !== "string" || !path.trim()) return "a path is required";
  if (typeof name !== "string" || !name.trim()) return "a deliverable name is required";
  if (name.trim().length > NAME_MAX) return `the deliverable name is at most ${NAME_MAX} characters`;
  if (!isPhase(phase)) return "the phase is 0 to 6";
  if (typeof iterate !== "boolean") return "say whether the deliverable is owed again in every later phase";
  const current = exceptId === undefined ? undefined : rows.find((r) => r.id === exceptId);
  const lists: [string, unknown, readonly string[]][] = [
    ["owner", input.ownerRoleIds, current?.ownerRoleIds ?? []],
    ["reviewer", input.reviewerRoleIds, current?.reviewerRoleIds ?? []],
    ["consulted", input.consultedRoleIds, current?.consultedRoleIds ?? []],
  ];
  for (const [label, list, recorded] of lists) {
    if (!isStringList(list)) return `${label} roles must be a list of role ids`;
    const reason = roleReason(list, roles, recorded);
    if (reason) return reason;
  }
  /* exact duplicate = a STANDING row with the same swimlane, path, phase and
     normalized name (PC3); the sheet repeats a name across phases on purpose */
  const wanted = normalizeName(name);
  const lane = normalizeName(swimlane);
  const way = normalizeName(path);
  const taken = standing(rows).find(
    (r) =>
      r.id !== exceptId &&
      r.phase === phase &&
      normalizeName(r.swimlane) === lane &&
      normalizeName(r.path) === way &&
      normalizeName(r.name) === wanted,
  );
  if (taken)
    return `a deliverable named "${taken.name}" already stands in ${taken.swimlane} · ${taken.path} for Phase ${taken.phase}`;
  /* The id is minted from these four fields and never re-minted (PC3), so a
     row that was renamed or moved to another phase still HOLDS the id this
     input would mint. Appending under it would bury that row — and every
     product's evidence pointed at its id — under a different obligation. The
     id keeps only the swimlane's NUMBER, so "12 - Test" and "12 - Other"
     mint the same id: a holder in a differently-named lane refuses whether
     it stands or is retired (appending under a retired one would mix two
     lanes' histories). A retired holder in the SAME lane does not block:
     re-adding the identity brings it back. */
  const minted = pdpDeliverableId(swimlane, path, phase, name);
  const holder = rows.find((r) => r.id !== exceptId && r.id === minted);
  if (holder && normalizeName(holder.swimlane) !== lane)
    return `swimlane "${collapse(swimlane)}" collides with "${holder.swimlane}" (they mint the same id) — use "${holder.swimlane}" or a different swimlane number`;
  if (holder && !holder.retired)
    return `that identity already belongs to "${holder.name}" (now ${holder.swimlane} · ${holder.path}, Phase ${holder.phase}) — edit it instead`;
  return null;
}

export function validatePdpDeliverableEdit(
  edit: PdpDeliverableEdit,
  rows: PdpDeliverable[],
  roles: Role[],
  id: string,
): string | null {
  const current = rows.find((r) => r.id === id);
  if (!current) return "that deliverable is not in the catalog";
  if (Object.keys(edit).length === 0) return NOTHING;
  if (edit.retired !== undefined && typeof edit.retired !== "boolean") return "retired must be a boolean";
  return validatePdpDeliverableInput({ ...current, ...edit }, rows, roles, id);
}

export const cleanPdpDeliverableInput = (input: PdpDeliverableInput): PdpDeliverableInput => ({
  swimlane: collapse(input.swimlane),
  path: collapse(input.path),
  name: collapse(input.name),
  phase: input.phase,
  iterate: input.iterate,
  ownerRoleIds: [...input.ownerRoleIds],
  reviewerRoleIds: [...input.reviewerRoleIds],
  consultedRoleIds: [...input.consultedRoleIds],
});

/* The id comes from the row being edited and is NEVER re-derived (PC3/CR12):
   re-minting it would orphan every product's evidence recorded against it. */
export function applyPdpDeliverableEdit(
  current: PdpDeliverable,
  edit: PdpDeliverableEdit,
  by: UserRef,
  date: string,
): PdpDeliverable {
  const merged = cleanPdpDeliverableInput({ ...current, ...edit });
  return {
    ...current,
    ...merged,
    id: current.id,
    version: current.version,
    retired: edit.retired ?? current.retired ?? false,
    recorded: { source: ENTERED_SRC, date, by },
  };
}

/* ---- criteria (PC9) ---- */

const targetReason = (target: unknown): string | null => {
  if (target === null || target === undefined) return null;
  if (typeof target !== "object") return TARGET_PARTIAL;
  const { op, value, unit } = target as Record<string, unknown>;
  if (!TARGET_OPS.includes(op as string)) return TARGET_PARTIAL;
  if (typeof value !== "number" || !Number.isFinite(value)) return TARGET_PARTIAL;
  if (typeof unit !== "string" || !unit.trim()) return TARGET_PARTIAL;
  return null;
};

export function validatePdpCriterionInput(
  input: { phase: unknown; area: unknown; text: unknown; unconfirmed: unknown; target: unknown },
  criteria: PdpCriterion[],
  exceptId?: string,
): string | null {
  const { phase, area, text, unconfirmed, target } = input;
  if (!isPhase(phase)) return "the phase is 0 to 6";
  if (typeof area !== "string" || !PDP_CRITERION_AREAS.includes(area))
    return "the area must be one of the PDP criterion areas";
  if (text !== null && text !== undefined && typeof text !== "string") return "the criterion text must be text";
  if (typeof text === "string" && text.trim().length > TEXT_MAX)
    return `the criterion text is at most ${TEXT_MAX} characters`;
  if (typeof unconfirmed !== "boolean") return "unconfirmed must be a boolean";
  const bad = targetReason(target);
  if (bad) return bad;
  const taken = criteria.find((c) => c.id !== exceptId && c.phase === phase && c.area === area);
  if (taken) return `a criterion for Phase ${phase} · ${area} already exists — edit it instead`;
  return null;
}

export function validatePdpCriterionEdit(edit: PdpCriterionEdit, criteria: PdpCriterion[], id: string): string | null {
  const current = criteria.find((c) => c.id === id);
  if (!current) return "that criterion is not in the catalog";
  if (Object.keys(edit).length === 0) return NOTHING;
  /* the body may not move the slot: phase and area mint the id (PC9) */
  if ("phase" in edit || "area" in edit) return "a criterion's phase and area are its identity — clear it and record the other slot instead";
  const { text, unconfirmed, target } = { ...current, ...edit };
  return validatePdpCriterionInput({ phase: current.phase, area: current.area, text, unconfirmed, target }, criteria, id);
}

/* an absent or blank text is NOT RECORDED (red), never an empty value */
const cleanText = (text: string | null | undefined): string | null =>
  typeof text === "string" && text.trim() ? text.trim() : null;

const cleanTarget = (target: PdpTarget | null | undefined): PdpTarget | null =>
  target ? { op: target.op, value: target.value, unit: target.unit.trim() } : null;

export const cleanPdpCriterionInput = (input: PdpCriterionInput): PdpCriterionInput => ({
  phase: input.phase,
  area: input.area,
  text: cleanText(input.text),
  unconfirmed: input.unconfirmed,
  target: cleanTarget(input.target),
});

export function applyPdpCriterionEdit(
  current: PdpCriterion,
  edit: PdpCriterionEdit,
  by: UserRef,
  date: string,
): PdpCriterion {
  return {
    ...current,
    ...cleanPdpCriterionInput({ ...current, ...edit }),
    id: current.id,
    version: current.version,
    phase: current.phase,
    area: current.area,
    recorded: { source: ENTERED_SRC, date, by },
  };
}

/* ---- gates (PC8) — never created, never retired; label and approver edit ---- */

export function validatePdpGateEdit(edit: PdpGateEdit, gates: PdpGate[], roles: Role[], id: string): string | null {
  const current = gates.find((g) => g.id === id);
  if (!current) return "that gate is not in the catalog";
  if (Object.keys(edit).length === 0) return NOTHING;
  if (edit.label !== undefined && (typeof edit.label !== "string" || !edit.label.trim()))
    return "a gate label is required";
  if (edit.approverRoleId !== undefined && edit.approverRoleId !== null) {
    if (typeof edit.approverRoleId !== "string") return "the approver must be a role id or null";
    const reason = roleReason([edit.approverRoleId], roles, current.approverRoleId ? [current.approverRoleId] : []);
    if (reason) return reason;
  }
  return null;
}

export function applyPdpGateEdit(current: PdpGate, edit: PdpGateEdit, by: UserRef, date: string): PdpGate {
  return {
    ...current,
    id: current.id,
    version: current.version,
    afterPhase: current.afterPhase,
    label: edit.label === undefined ? current.label : collapse(edit.label),
    approverRoleId: edit.approverRoleId === undefined ? current.approverRoleId : edit.approverRoleId,
    recorded: { source: ENTERED_SRC, date, by },
  };
}
